import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";

import {
  Dropdown,
  DropdownMenu,
  DropdownItem,
  UncontrolledDropdown,
  DropdownToggle,
  Navbar,
  Nav,
  Container,
  Media,
  Badge,
  Button,
} from "reactstrap";

import Avatar from "../../components/Common/Avatar";
import {
  getadmindetaildbyid,
  getAdminNotifications,
  markAdminNotificationRead,
  markAllAdminNotificationsRead,
  clearAllAdminNotifications,
} from "../../api/ApiServices";
import { useSocket } from "../../context/SocketContext";
import Swal from "sweetalert2";
import "../../assets/css/dashboard-redesign.css";

// Get image base URL from environment variable
const IMAGE_BASE_URL = process.env.REACT_APP_IMAGE_BASE_URL || "";

// Get admin ID from localStorage
const getAdminId = () => {
  const adminId = localStorage.getItem("adminId");
  if (adminId) return adminId;

  const userData = localStorage.getItem("userData");
  if (userData) {
    try {
      const parsed = JSON.parse(userData);
      return parsed.uuid || parsed.id || parsed._id || null;
    } catch (e) {
      return null;
    }
  }
  return null;
};

const AdminNavbar = (props) => {
  const { brandText, toggleSidebar } = props;

  const navigate = useNavigate();

  // =========================================================
  // Admin Data State
  // =========================================================
  const [admin, setAdmin] = useState({
    name: "Admin User",
    image: "",
    fullName: "",
    email: "",
    uuid: "",
  });

  const [loading, setLoading] = useState(true);

  // =========================================================
  // Notification State
  // =========================================================
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const [filterTab, setFilterTab] = useState("all");
  const [notifDropdownOpen, setNotifDropdownOpen] = useState(false);
  const socket = useSocket();

  // =========================================================
  // Helper: Get Full Image URL
  // =========================================================
  const getImageUrl = (imagePath) => {
    if (!imagePath) return "";

    let clean = String(imagePath).trim().replace(/^["']|["']$/g, "").replace(/%22$/gi, "");
    if (!clean) return "";

    if (clean.startsWith("blob:") || clean.startsWith("http://") || clean.startsWith("https://")) {
      return clean;
    }

    const cleanPath = clean.replace(/^\/+/, "");
    if (cleanPath.startsWith("admin-profile-images/") || cleanPath.startsWith("users-profile-images/")) {
      return `https://areatrek-live-bucket.s3.us-east-2.amazonaws.com/${cleanPath}`;
    }
    return `https://areatrek-live-bucket.s3.us-east-2.amazonaws.com/admin-profile-images/${cleanPath}`;
  };

  // =========================================================
  // Fetch Admin Details
  // =========================================================
  const fetchAdminDetails = async () => {
    try {
      const adminId = getAdminId();

      if (!adminId) {
        console.log(" Admin ID not found in localStorage");
        setLoading(false);
        return;
      }

      const response = await getadmindetaildbyid(adminId);

      if (!response || !response.success) {
        console.log(" Failed to fetch admin details");
        setLoading(false);
        return;
      }

      let adminData = response?.data || response?.payload || response;

      if (Array.isArray(adminData)) {
        adminData = adminData[0] || {};
      }

      if (!adminData || Object.keys(adminData).length === 0) {
        console.log(" No admin data found");
        setLoading(false);
        return;
      }

      // Get image URL
      const imagePath = adminData.imageUrl || adminData.image || adminData.profileImage || "";
      const imageUrl = getImageUrl(imagePath);

      // Get display name
      const displayName = adminData.fullName || adminData.name || "Admin User";

      // Sync updated permissions and user metadata to localStorage
      if (Array.isArray(adminData.permissions) && adminData.permissions.length > 0) {
        localStorage.setItem("userPermissions", JSON.stringify(adminData.permissions));
      }
      if (adminData.roleName) {
        localStorage.setItem("userRole", adminData.roleName);
      }
      try {
        const storedUserData = JSON.parse(localStorage.getItem("userData") || "{}");
        const updatedUserData = { ...storedUserData, ...adminData };
        localStorage.setItem("userData", JSON.stringify(updatedUserData));
      } catch (e) {}

      setAdmin({
        name: displayName,
        fullName: displayName,
        image: imageUrl,
        email: adminData.email || "",
        uuid: adminData.uuid || adminData._id || "",
      });

    } catch (error) {
      console.error(" Error fetching admin in navbar:", error);
    } finally {
      setLoading(false);
    }
  };

  // =========================================================
  // Load Admin Data on Mount
  // =========================================================
  useEffect(() => {
    fetchAdminDetails();

    // Listen for image updates from Profile page
    const handleImageUpdate = (event) => {
      setAdmin((prev) => ({
        ...prev,
        image: event.detail,
      }));
    };

    // Listen for admin data updates
    const handleAdminUpdate = (event) => {
      if (event.detail) {
        setAdmin((prev) => ({
          ...prev,
          name: event.detail.fullName || prev.name,
          fullName: event.detail.fullName || prev.fullName,
          email: event.detail.email || prev.email,
          image: event.detail.image || prev.image,
        }));
      }
    };

    window.addEventListener("adminProfileImageUpdated", handleImageUpdate);
    window.addEventListener("adminDataUpdated", handleAdminUpdate);

    return () => {
      window.removeEventListener("adminProfileImageUpdated", handleImageUpdate);
      window.removeEventListener("adminDataUpdated", handleAdminUpdate);
    };
  }, []);

  // =========================================================
  // Notification Handlers & Real-Time Socket
  // =========================================================
  const fetchNotifications = async () => {
    try {
      setLoadingNotifications(true);
      const res = await getAdminNotifications(30);
      if (res && res.success) {
        setNotifications(res.data || []);
        setUnreadCount(res.unreadCount || 0);
      }
    } catch (err) {
      console.error("Failed to fetch admin notifications:", err);
    } finally {
      setLoadingNotifications(false);
    }
  };

  // Web Audio API notification chime generator
  const playNotificationChime = () => {
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return;
      const ctx = new AudioCtx();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = "sine";
      osc.frequency.setValueAtTime(880, ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(1320, ctx.currentTime + 0.12);

      gain.gain.setValueAtTime(0.2, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4);

      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.start();
      osc.stop(ctx.currentTime + 0.45);
    } catch (e) {
      // Audio autoplay policy fallback
    }
  };

  useEffect(() => {
    fetchNotifications();

    // Heartbeat polling every 12 seconds as backup
    const interval = setInterval(fetchNotifications, 12000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!socket) return;

    const handleNewVerification = (data) => {
      console.log("🔔 Real-time verification notification received in navbar:", data);

      // 1. Play subtle audio chime
      playNotificationChime();

      // 2. Prepend to state immediately for 0ms UI response
      if (data) {
        setNotifications((prev) => {
          const exists = prev.some(
            (n) =>
              n.uuid === data.verificationUuid ||
              n.data?.verificationUuid === data.verificationUuid
          );
          if (exists) return prev;

          const newNotif = {
            uuid: data.verificationUuid || `v_${Date.now()}`,
            title: data.title || "New Document Verification Request",
            body:
              data.body ||
              `${data.guideName || "A guide"} has submitted documents for verification.`,
            type: "verification_submitted",
            createdAt: data.createdAt || new Date().toISOString(),
            isRead: false,
            data: {
              verificationUuid: data.verificationUuid,
              guideName: data.guideName,
              guideEmail: data.guideEmail,
            },
          };
          return [newNotif, ...prev];
        });
        setUnreadCount((prev) => prev + 1);
      }

      // 3. Show modern toast alert
      Swal.fire({
        toast: true,
        position: "top-end",
        showConfirmButton: true,
        confirmButtonText: "Review Now →",
        confirmButtonColor: "#0f766e",
        showCancelButton: true,
        cancelButtonText: "Dismiss",
        timer: 9000,
        timerProgressBar: true,
        icon: "info",
        title: "New Verification Submitted",
        text: `${data.guideName || "A guide"} (${data.guideEmail || ""}) submitted documents for review.`,
      }).then((result) => {
        if (result.isConfirmed) {
          navigate("/admin/verification-management");
        }
      });

      // 4. Dispatch global event for active pages (e.g. GuideManagement table)
      window.dispatchEvent(
        new CustomEvent("newVerificationSubmitted", { detail: data })
      );

      // 5. Sync with backend API
      fetchNotifications();
    };

    const handleNewSupportMessage = (data) => {
      console.log("🔔 Real-time support message received in navbar:", data);

      playNotificationChime();

      if (data) {
        setNotifications((prev) => {
          const exists = prev.some(
            (n) =>
              (n.uuid && n.uuid === data.conversationUuid) ||
              (n.data && n.data.conversationUuid === data.conversationUuid && n.body === data.body)
          );
          if (exists) return prev;

          const newNotif = {
            uuid: `support_${data.conversationUuid || Date.now()}_${Date.now()}`,
            title: data.title || "Help & Support Message",
            body: data.body || `${data.senderName || "A user"} sent a message on Help & Support.`,
            type: "support_message",
            createdAt: data.createdAt || new Date().toISOString(),
            isRead: false,
            data: {
              conversationUuid: data.conversationUuid,
              userUuid: data.userUuid,
              senderName: data.senderName,
            },
          };
          return [newNotif, ...prev];
        });
        setUnreadCount((prev) => prev + 1);
      }

      Swal.fire({
        toast: true,
        position: "top-end",
        showConfirmButton: true,
        confirmButtonText: "Reply Now →",
        confirmButtonColor: "#0f766e",
        showCancelButton: true,
        cancelButtonText: "Dismiss",
        timer: 8000,
        timerProgressBar: true,
        icon: "info",
        title: "Help & Support Notification",
        text: data.body || `${data.senderName || "A user"} sent a message.`,
      }).then((result) => {
        if (result.isConfirmed) {
          navigate("/admin/help-support", {
            state: {
              conversationUuid: data.conversationUuid,
              userUuid: data.userUuid,
              timestamp: Date.now(),
            },
          });
        }
      });

      fetchNotifications();
    };

    socket.on("new_verification_submitted", handleNewVerification);
    socket.on("verification_status_updated", handleNewVerification);
    socket.on("new_support_notification", handleNewSupportMessage);

    return () => {
      socket.off("new_verification_submitted", handleNewVerification);
      socket.off("verification_status_updated", handleNewVerification);
      socket.off("new_support_notification", handleNewSupportMessage);
    };
  }, [socket, navigate]);

  const handleMarkAllAsRead = async (e) => {
    if (e) e.stopPropagation();
    try {
      await markAllAdminNotificationsRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error("Failed to mark all as read:", err);
    }
  };

  const handleMarkSingleAsRead = async (e, notif) => {
    if (e) e.stopPropagation();
    try {
      await markAdminNotificationRead(notif.uuid);
      setNotifications((prev) =>
        prev.map((n) => (n.uuid === notif.uuid ? { ...n, isRead: true } : n))
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (err) {
      console.error("Failed to mark notification as read:", err);
    }
  };

  const handleClearAll = async (e) => {
    if (e) e.stopPropagation();
    const result = await Swal.fire({
      title: "Clear all notifications?",
      text: "Are you sure you want to clear all notifications? This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#64748b",
      confirmButtonText: "Yes, clear all",
      cancelButtonText: "Cancel",
      reverseButtons: true,
    });

    if (result.isConfirmed) {
      try {
        await clearAllAdminNotifications();
        setNotifications([]);
        setUnreadCount(0);
        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "success",
          title: "Notifications cleared",
          showConfirmButton: false,
          timer: 2000,
        });
      } catch (err) {
        console.error("Failed to clear notifications:", err);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to clear notifications.",
        });
      }
    }
  };

  const handleNotificationClick = async (notif) => {
    try {
      // Close notification dropdown immediately
      setNotifDropdownOpen(false);

      if (!notif.isRead) {
        await markAdminNotificationRead(notif.uuid);
        setNotifications((prev) =>
          prev.map((n) => (n.uuid === notif.uuid ? { ...n, isRead: true } : n))
        );
        setUnreadCount((prev) => Math.max(0, prev - 1));
      }

      const type = (notif.type || "").toLowerCase();
      const title = (notif.title || "").toLowerCase();
      const body = (notif.body || "").toLowerCase();
      const data = notif.data || {};

      // Determine target page based on notification type and contents
      if (
        type.includes("verification") ||
        type.includes("document") ||
        data.verificationUuid ||
        title.includes("verification") ||
        title.includes("document") ||
        body.includes("verification")
      ) {
        navigate("/admin/verification-management", {
          state: {
            verificationUuid: data.verificationUuid,
            guideUuid: data.guideUuid || data.userUuid,
            searchTerm: data.guideName || data.guideEmail || "",
            statusFilter: type.includes("submitted")
              ? "pending"
              : type.includes("approved")
              ? "approved"
              : type.includes("rejected")
              ? "rejected"
              : "all",
            autoOpen: true,
            timestamp: Date.now(),
          },
        });

        // Trigger custom event so page updates immediately if admin is already on Verification Management
        window.dispatchEvent(
          new CustomEvent("verificationNotificationClicked", {
            detail: {
              verificationUuid: data.verificationUuid,
              guideUuid: data.guideUuid || data.userUuid,
              guideName: data.guideName,
              guideEmail: data.guideEmail,
              status: type.includes("submitted") ? "pending" : "all",
            },
          })
        );
      } else if (
        type.includes("booking") ||
        title.includes("booking") ||
        body.includes("booking")
      ) {
        navigate("/admin/guide-bookings", {
          state: {
            bookingUuid: data.bookingUuid,
            statusFilter: data.status || "all",
            timestamp: Date.now(),
          },
        });
      } else if (
        type.includes("rating") ||
        type.includes("review") ||
        title.includes("rating") ||
        title.includes("review") ||
        body.includes("rating") ||
        body.includes("review")
      ) {
        navigate("/admin/reviews", {
          state: {
            ratingUuid: data.ratingUuid || data.reviewUuid,
            timestamp: Date.now(),
          },
        });
      } else if (
        type.includes("message") ||
        type.includes("chat") ||
        title.includes("message") ||
        title.includes("chat")
      ) {
        navigate("/admin/help-support", {
          state: {
            conversationUuid: data.conversationUuid || data.conversationId || data.chatId,
            userUuid: data.userUuid,
            timestamp: Date.now(),
          },
        });
      } else if (
        type.includes("user") ||
        title.includes("user")
      ) {
        navigate("/admin/users-list", {
          state: {
            userUuid: data.userUuid,
            timestamp: Date.now(),
          },
        });
      } else if (
        type.includes("package") ||
        type.includes("tour")
      ) {
        navigate("/admin/tour-packages");
      } else if (
        type.includes("membership")
      ) {
        navigate("/admin/membership");
      } else if (
        type.includes("support") ||
        title.includes("support") ||
        body.includes("support")
      ) {
        navigate("/admin/help-support", {
          state: {
            conversationUuid: data.conversationUuid,
            userUuid: data.userUuid,
            timestamp: Date.now(),
          },
        });
      } else if (
        type.includes("report")
      ) {
        navigate("/admin/report");
      } else {
        navigate("/admin/verification-management");
      }
    } catch (err) {
      console.error("Notification click error:", err);
    }
  };

  const formatTimeAgo = (dateStr) => {
    if (!dateStr) return "";
    const diffSec = Math.floor((new Date() - new Date(dateStr)) / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHour = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHour / 24);

    if (diffSec < 60) return "Just now";
    if (diffMin < 60) return `${diffMin}m ago`;
    if (diffHour < 24) return `${diffHour}h ago`;
    if (diffDay < 7) return `${diffDay}d ago`;
    return new Date(dateStr).toLocaleDateString();
  };

  const getNotificationIcon = (type) => {
    if (type?.includes("verification") || type?.includes("document"))
      return "ni ni-badge";
    if (type?.includes("support")) return "ni ni-headphones";
    if (type?.includes("booking")) return "ni ni-calendar-grid-58";
    if (type?.includes("chat") || type?.includes("message"))
      return "ni ni-chat-round";
    return "ni ni-bell-55";
  };

  const getNotificationBadgeStyle = (type) => {
    if (type === "verification_submitted" || type === "document_submitted") {
      return {
        background: "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)",
        color: "#b45309",
      };
    }
    if (type === "verification_approved" || type === "document_approved") {
      return {
        background: "linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)",
        color: "#047857",
      };
    }
    if (type === "verification_rejected" || type === "document_rejected") {
      return {
        background: "linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)",
        color: "#b91c1c",
      };
    }
    if (type?.includes("support")) {
      return {
        background: "linear-gradient(135deg, #ede9fe 0%, #ddd6fe 100%)",
        color: "#6d28d9",
      };
    }
    return {
      background: "linear-gradient(135deg, #e0f2fe 0%, #bae6fd 100%)",
      color: "#0369a1",
    };
  };

  // =========================================================
  // Logout
  // =========================================================
  const handleLogout = () => {
    // Remove authentication/session data
    localStorage.removeItem("token");
    localStorage.removeItem("adminId");
    localStorage.removeItem("userData");
    localStorage.removeItem("user");
    localStorage.removeItem("adminPermissions");
    localStorage.removeItem("userRole");
    localStorage.removeItem("userPermissions");

    // Redirect to login
    navigate("/auth/login", {
      replace: true,
    });
  };

  return (
    <Navbar
      className="navbar-dark sticky-top bg-gradient-primary-1-navbar"
      expand="md"
      id="navbar-main"
      style={{ zIndex: 1050 }}
    >
      <Container
        fluid
        className="d-flex align-items-center px-lg-4"
      >
        <div className="navbar-toggle-container d-none d-md-block mr-3">
          <div
            className="sidebar-toggle-btn-top"
            onClick={toggleSidebar}
            style={{
              cursor: "pointer",
              color: "white",
              fontSize: "1.5rem",
            }}
          >
            <i className="ni ni-align-left-2" />
          </div>
        </div>
        <Link
          className="h4 mb-0 text-white text-uppercase d-none d-lg-inline-flex align-items-center"
          to="/"
          style={{
            minWidth: "max-content",
            letterSpacing: "0.05em",
          }}
        >
          {brandText}
        </Link>
        <Nav
          className="align-items-center d-none d-md-flex ml-auto"
          navbar
        >
          {/* Notification Bell Dropdown */}
          <Dropdown
            nav
            className="mr-3"
            isOpen={notifDropdownOpen}
            toggle={() => setNotifDropdownOpen((prev) => !prev)}
          >
            <DropdownToggle
              nav
              className="p-0 position-relative"
              style={{ cursor: "pointer", background: "none", border: "none" }}
            >
              <div
                className="notification-bell-btn"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  position: "relative",
                  padding: "6px 8px",
                  background: "transparent",
                  border: "none",
                }}
                title="Notifications"
              >
                <i
                  className="ni ni-bell-55"
                  style={{
                    color: "#ffffff",
                    fontSize: "1.35rem",
                    filter: "drop-shadow(0 2px 4px rgba(0, 0, 0, 0.25))",
                  }}
                />
                {unreadCount > 0 && (
                  <span
                    className="notification-badge-pulse"
                    style={{
                      position: "absolute",
                      top: "0px",
                      right: "0px",
                      background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                      color: "#ffffff",
                      borderRadius: "10px",
                      padding: "2px 5px",
                      fontSize: "9.5px",
                      fontWeight: "800",
                      boxShadow: "0 2px 8px rgba(239, 68, 68, 0.6)",
                      border: "1.5px solid #ffffff",
                      lineHeight: "1",
                      minWidth: "16px",
                      textAlign: "center",
                    }}
                  >
                    {unreadCount > 99 ? "99+" : unreadCount}
                  </span>
                )}
              </div>
            </DropdownToggle>

            <DropdownMenu
              right
              className="notification-dropdown-menu dropdown-menu-arrow"
              style={{
                width: "420px",
                maxWidth: "94vw",
                borderRadius: "18px",
                background: "#ffffff",
                border: "1px solid #e2e8f0",
                boxShadow:
                  "0 25px 50px -12px rgba(15, 23, 42, 0.25), 0 0 0 1px rgba(226, 232, 240, 0.8)",
                padding: 0,
                marginTop: "12px",
                overflow: "hidden",
                zIndex: 1060,
              }}
            >
              {/* Header Top Bar */}
              <div
                style={{
                  padding: "16px 20px 12px 20px",
                  background: "#ffffff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <div className="d-flex align-items-center">
                  <div
                    style={{
                      width: "36px",
                      height: "36px",
                      borderRadius: "10px",
                      background: "linear-gradient(135deg, #0f766e 0%, #14b8a6 100%)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginRight: "10px",
                      boxShadow: "0 4px 10px rgba(15, 118, 110, 0.25)",
                    }}
                  >
                    <i
                      className="ni ni-bell-55"
                      style={{ color: "#ffffff", fontSize: "15px" }}
                    />
                  </div>
                  <div>
                    <div className="d-flex align-items-center">
                      <span
                        style={{
                          fontWeight: 700,
                          fontSize: "15px",
                          color: "#0f172a",
                        }}
                      >
                        Notifications
                      </span>
                      {unreadCount > 0 ? (
                        <span
                          className="badge badge-pill ml-2"
                          style={{
                            backgroundColor: "#fef2f2",
                            color: "#dc2626",
                            border: "1px solid #fecaca",
                            fontSize: "10.5px",
                            fontWeight: 700,
                            padding: "3px 8px",
                          }}
                        >
                          {unreadCount} New
                        </span>
                      ) : (
                        <span
                          className="badge badge-pill ml-2"
                          style={{
                            backgroundColor: "#f0fdfa",
                            color: "#0f766e",
                            border: "1px solid #ccfbf1",
                            fontSize: "10.5px",
                            fontWeight: 600,
                            padding: "3px 8px",
                          }}
                        >
                          All caught up
                        </span>
                      )}
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        fontSize: "11px",
                        color: "#16a34a",
                        fontWeight: 600,
                        marginTop: "1px",
                      }}
                    >
                      <span
                        className="pulse-dot"
                        style={{ width: "6px", height: "6px" }}
                      />
                      <span>Live Updates Active</span>
                    </div>
                  </div>
                </div>

                {unreadCount > 0 && (
                  <button
                    onClick={handleMarkAllAsRead}
                    style={{
                      background: "#f0fdfa",
                      border: "1px solid #ccfbf1",
                      borderRadius: "6px",
                      padding: "5px 10px",
                      color: "#0f766e",
                      fontSize: "11.5px",
                      fontWeight: 700,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                      transition: "all 0.2s ease",
                    }}
                    title="Mark all notifications as read"
                  >
                    <i className="ni ni-check-bold" style={{ fontSize: "10px" }} />
                    Mark all read
                  </button>
                )}
              </div>

              {/* Filter Tabs Bar */}
              <div className="notification-tabs-bar">
                <button
                  type="button"
                  className={`notification-tab-pill ${filterTab === "all" ? "active" : ""}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setFilterTab("all");
                  }}
                >
                  All{" "}
                  <span className="tab-count-badge">
                    {notifications.length}
                  </span>
                </button>
                <button
                  type="button"
                  className={`notification-tab-pill ${
                    filterTab === "support" ? "active" : ""
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setFilterTab("support");
                  }}
                  title="Help & Support"
                >
                  <i className="ni ni-headphones" style={{ fontSize: "11px" }} />
                  Support{" "}
                  <span className="tab-count-badge">
                    {
                      notifications.filter(
                        (n) =>
                          n.type?.includes("support") ||
                          n.title?.toLowerCase().includes("support") ||
                          n.body?.toLowerCase().includes("support") ||
                          n.data?.conversationUuid
                      ).length
                    }
                  </span>
                </button>
                <button
                  type="button"
                  className={`notification-tab-pill ${
                    filterTab === "verifications" ? "active" : ""
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setFilterTab("verifications");
                  }}
                >
                  Verifications{" "}
                  <span className="tab-count-badge">
                    {
                      notifications.filter(
                        (n) =>
                          n.type?.includes("verification") ||
                          n.type?.includes("document") ||
                          n.data?.verificationUuid
                      ).length
                    }
                  </span>
                </button>
                <button
                  type="button"
                  className={`notification-tab-pill ${filterTab === "unread" ? "active" : ""}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setFilterTab("unread");
                  }}
                >
                  Unread{" "}
                  <span className="tab-count-badge">{unreadCount}</span>
                </button>
              </div>

              {/* Notifications Scrollable List */}
              <div className="notification-scroll-container">
                {loadingNotifications && notifications.length === 0 ? (
                  <div className="text-center py-5 text-muted">
                    <span
                      className="spinner-border spinner-border-sm"
                      style={{ color: "#0f766e" }}
                    />
                    <p
                      className="mb-0 text-xs mt-2"
                      style={{ color: "#64748b" }}
                    >
                      Loading notifications...
                    </p>
                  </div>
                ) : (() => {
                  const itemsToShow = notifications.filter((item) => {
                    if (filterTab === "unread") return !item.isRead;
                    if (filterTab === "verifications") {
                      return (
                        item.type?.includes("verification") ||
                        item.type?.includes("document") ||
                        item.data?.verificationUuid
                      );
                    }
                    if (filterTab === "support") {
                      return (
                        item.type?.includes("support") ||
                        item.title?.toLowerCase().includes("support") ||
                        item.body?.toLowerCase().includes("support") ||
                        item.data?.conversationUuid
                      );
                    }
                    return true;
                  });

                  if (itemsToShow.length === 0) {
                    return (
                      <div className="notification-empty-box">
                        <div
                          style={{
                            width: "60px",
                            height: "60px",
                            borderRadius: "50%",
                            background:
                              "linear-gradient(135deg, #f0fdfa 0%, #ccfbf1 100%)",
                            border: "5px solid #f0fdfa",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            margin: "0 auto 14px auto",
                            boxShadow:
                              "0 6px 16px -2px rgba(15, 118, 110, 0.15)",
                          }}
                        >
                          <i
                            className={
                              filterTab === "support"
                                ? "ni ni-headphones"
                                : filterTab === "verifications"
                                ? "ni ni-badge"
                                : filterTab === "unread"
                                ? "ni ni-check-bold"
                                : "ni ni-bell-55"
                            }
                            style={{ fontSize: "1.5rem", color: "#0f766e" }}
                          />
                        </div>
                        <h5
                          style={{
                            fontWeight: 700,
                            color: "#0f172a",
                            fontSize: "15px",
                            marginBottom: "4px",
                          }}
                        >
                          {filterTab === "support"
                            ? "No Support Messages"
                            : filterTab === "verifications"
                            ? "No Verification Alerts"
                            : filterTab === "unread"
                            ? "No Unread Notifications"
                            : "All Caught Up!"}
                        </h5>
                        <p
                          style={{
                            fontSize: "13px",
                            color: "#64748b",
                            maxWidth: "280px",
                            margin: "0 auto 16px auto",
                            lineHeight: 1.5,
                          }}
                        >
                          {filterTab === "support"
                            ? "No user support chats or help desk inquiries pending."
                            : filterTab === "verifications"
                            ? "No guide verification documents currently pending review."
                            : filterTab === "unread"
                            ? "You have reviewed all current notifications."
                            : "New guide document submissions and system alerts will appear here in real time."}
                        </p>
                        {filterTab === "support" ? (
                          <Link
                            to="/admin/help-support"
                            onClick={() => setNotifDropdownOpen(false)}
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "6px",
                              fontSize: "12px",
                              fontWeight: 600,
                              color: "#0f766e",
                              background: "#f0fdfa",
                              padding: "6px 14px",
                              borderRadius: "20px",
                              border: "1px solid #99f6e4",
                              textDecoration: "none",
                              transition: "all 0.2s ease",
                            }}
                          >
                            <i className="ni ni-headphones" />
                            Open Help & Support →
                          </Link>
                        ) : (
                          <Link
                            to="/admin/verification-management"
                            onClick={() => setNotifDropdownOpen(false)}
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "6px",
                              fontSize: "12px",
                              fontWeight: 600,
                              color: "#0f766e",
                              background: "#f0fdfa",
                              padding: "6px 14px",
                              borderRadius: "20px",
                              border: "1px solid #99f6e4",
                              textDecoration: "none",
                              transition: "all 0.2s ease",
                            }}
                          >
                            <i className="ni ni-badge" />
                            View Verification Pipeline →
                          </Link>
                        )}
                      </div>
                    );
                  }

                  return itemsToShow.map((item) => (
                    <div
                      key={item.uuid}
                      className={`notification-item-card ${
                        !item.isRead ? "unread" : "read"
                      }`}
                      onClick={() => handleNotificationClick(item)}
                    >
                      <div
                        style={{
                          width: "38px",
                          height: "38px",
                          borderRadius: "10px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          marginRight: "14px",
                          flexShrink: 0,
                          boxShadow: "0 2px 6px rgba(0, 0, 0, 0.06)",
                          ...getNotificationBadgeStyle(item.type),
                        }}
                      >
                        <i
                          className={getNotificationIcon(item.type)}
                          style={{ fontSize: "16px" }}
                        />
                      </div>

                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            marginBottom: "4px",
                          }}
                        >
                          <h6
                            style={{
                              margin: 0,
                              fontSize: "13.5px",
                              fontWeight: !item.isRead ? 700 : 600,
                              color: !item.isRead ? "#0f172a" : "#334155",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              maxWidth: "220px",
                            }}
                          >
                            {item.title}
                          </h6>
                          <span
                            style={{
                              fontSize: "11px",
                              color: "#64748b",
                              background: "#f1f5f9",
                              padding: "2px 7px",
                              borderRadius: "10px",
                              fontWeight: 500,
                              whiteSpace: "nowrap",
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "4px",
                            }}
                          >
                            <i
                              className="ni ni-time-alarm"
                              style={{ fontSize: "10px" }}
                            />
                            {formatTimeAgo(item.createdAt)}
                          </span>
                        </div>

                        <p
                          style={{
                            margin: 0,
                            fontSize: "12.5px",
                            color: "#64748b",
                            lineHeight: 1.45,
                            wordBreak: "break-word",
                          }}
                        >
                          {item.body}
                        </p>

                        <div
                          style={{
                            marginTop: "8px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                        >
                          {(item.type?.includes("verification") ||
                            item.type?.includes("document") ||
                            item.data?.verificationUuid ||
                            item.title?.toLowerCase().includes("verification") ||
                            item.title?.toLowerCase().includes("document")) ? (
                            <span
                              className="notification-action-tag"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleNotificationClick(item);
                              }}
                            >
                              <i
                                className="ni ni-badge"
                                style={{ fontSize: "10px" }}
                              />
                              Review Documents →
                            </span>
                          ) : item.type?.includes("booking") ||
                            item.title?.toLowerCase().includes("booking") ? (
                            <span
                              className="notification-action-tag"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleNotificationClick(item);
                              }}
                            >
                              <i
                                className="ni ni-calendar-grid-58"
                                style={{ fontSize: "10px" }}
                              />
                              View Booking →
                            </span>
                          ) : item.type?.includes("rating") ||
                            item.type?.includes("review") ? (
                            <span
                              className="notification-action-tag"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleNotificationClick(item);
                              }}
                            >
                              <i
                                className="ni ni-favourite-28"
                                style={{ fontSize: "10px" }}
                              />
                              View Reviews →
                            </span>
                          ) : item.type?.includes("support") ||
                            item.title?.toLowerCase().includes("support") ||
                            item.body?.toLowerCase().includes("support") ? (
                            <span
                              className="notification-action-tag"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleNotificationClick(item);
                              }}
                            >
                              <i
                                className="ni ni-headphones"
                                style={{ fontSize: "10px" }}
                              />
                              Open Support Chat →
                            </span>
                          ) : item.type?.includes("message") ||
                            item.type?.includes("chat") ? (
                            <span
                              className="notification-action-tag"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleNotificationClick(item);
                              }}
                            >
                              <i
                                className="ni ni-chat-round"
                                style={{ fontSize: "10px" }}
                              />
                              Open Chat →
                            </span>
                          ) : (
                            <span
                              className="notification-action-tag"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleNotificationClick(item);
                              }}
                            >
                              <i
                                className="ni ni-bold-right"
                                style={{ fontSize: "10px" }}
                              />
                              View Details →
                            </span>
                          )}

                          {!item.isRead && (
                            <button
                              type="button"
                              onClick={(e) => handleMarkSingleAsRead(e, item)}
                              style={{
                                background: "none",
                                border: "none",
                                padding: "2px 6px",
                                color: "#0f766e",
                                fontSize: "11px",
                                fontWeight: 600,
                                cursor: "pointer",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "3px",
                              }}
                              title="Mark as read"
                            >
                              <i
                                className="ni ni-check-bold"
                                style={{ fontSize: "9px" }}
                              />
                              Mark read
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ));
                })()}
              </div>

              {/* Footer */}
              <div
                style={{
                  padding: "11px 18px",
                  background: "#f8fafc",
                  borderTop: "1px solid #f1f5f9",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                {notifications.length > 0 ? (
                  <button
                    type="button"
                    onClick={handleClearAll}
                    style={{
                      background: "none",
                      border: "none",
                      padding: 0,
                      color: "#94a3b8",
                      fontSize: "11.5px",
                      fontWeight: 600,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                      transition: "color 0.2s ease",
                    }}
                    onMouseEnter={(e) => (e.target.style.color = "#ef4444")}
                    onMouseLeave={(e) => (e.target.style.color = "#94a3b8")}
                  >
                    <i
                      className="ni ni-fat-remove"
                      style={{ fontSize: "11px" }}
                    />
                    Clear all
                  </button>
                ) : (
                  <span
                    style={{
                      fontSize: "11.5px",
                      color: "#94a3b8",
                      fontWeight: 500,
                    }}
                  >
                    Verification Alerts
                  </span>
                )}

                <Link
                  to="/admin/verification-management"
                  onClick={() => setNotifDropdownOpen(false)}
                  style={{
                    fontSize: "12px",
                    fontWeight: 700,
                    color: "#0f766e",
                    textDecoration: "none",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  Manage All Pipeline →
                </Link>
              </div>
            </DropdownMenu>
          </Dropdown>

          <UncontrolledDropdown nav>
            <DropdownToggle className="pr-0" nav>
              <Media className="align-items-center">
                <span className="avatar avatar-sm rounded-circle">
                  <Avatar
                    src={admin.image}
                    name={admin.name}
                    size="sm"
                  />
                </span>

                <Media className="ml-2 d-none d-lg-block">
                  <span className="mb-0 text-sm font-weight-bold text-white">
                    {admin.name}
                  </span>

                </Media>
              </Media>
            </DropdownToggle>

            <DropdownMenu className="dropdown-menu-arrow" right>
              <DropdownItem className="noti-title" header tag="div">
                <h6 className="text-overflow m-0">
                  Welcome, {admin.name}!
                </h6>
              </DropdownItem>

              <DropdownItem divider />

              {/* My Profile */}
              <DropdownItem to="/admin/profile" tag={Link}>
                <i className="ni ni-single-02" />
                <span>My Profile</span>
              </DropdownItem>

              <DropdownItem divider />

              {/* Logout */}
              <DropdownItem onClick={handleLogout}>
                <i className="ni ni-user-run" />
                <span>Logout</span>
              </DropdownItem>
            </DropdownMenu>
          </UncontrolledDropdown>
        </Nav>
      </Container>
    </Navbar>
  );
};

export default AdminNavbar;